import { Link } from 'react-router-dom';
import { MessageCircleIcon } from 'lucide-react';
import { RoutineCard } from '../shared/RoutineCard';
import { Faq } from '../shared/Faq';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { HeadingText } from '../shared/HeadingText';
import { PageHero } from '../shared/PageHero';
import { shopFaq } from '../../data/faq';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder, salon } from '../../data/content';
import { SALON_CODE } from '../../data/codes';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { mediaOr } from '../../core/rules/media';
import { shopLabels } from '../../data/shopLabels';
import { btnOnClay, btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { closingHeading, sectionHeading } from '../../ui/sections';
import { plural } from '../../core/format/text';
import { RoutineCompare } from './RoutineCompare';
import { RoutineAnchor } from './RoutineAnchor';
import { GiftTierList } from './GiftTierList';
import { anchorRoutine, ROUTINES_FAQ_COUNT } from './routineRules';

/**
 * Protocollen: de pagina met de hoogste orderwaarde. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wat is een protocol (hero, sand) · hoe werkt het en wat krijg ik
 * erbij (shell) · het complete protocol (blush) · of begin bij je klacht, en vergelijk (sand) ·
 * abonnement (shell) · wat als het niet past (sand) · al klant in de salon? (klei).
 */
export function Routines() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    routines,
    routineTotals
  } = useCatalog();
  const copy = pages.routines;
  const complete = anchorRoutine(routines, r => routineTotals(r).price);
  const others = routines.filter(r => r.id !== complete?.id);
  const heroMedia = mediaOr(copy.hero.media, {
    image: complete?.image || founder.withProducts,
    alt: complete ? `De producten van ${complete.name} op een rij` : `${founder.name} met de producten waar ze mee werkt`
  });
  return <>
      {/* Hero: zelfde opbouw als salon en over. In het beeld: wie het opstelt, en wat je meekrijgt. */}
      <PageHero titleId="routines-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8314" data-mp="20x814ijum5mv" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              Huidplan op papier, opgesteld door <span className="text-ink" data-id="element-8315" data-mp="2emtcp7vbjikv">{founder.firstName}</span>
            </>,
      sub: `${plural(routines.length, 'protocol', 'protocollen')} · 100% vegan`
    }} actions={(copy.hero.cta || copy.hero.secondaryCta) && <>
              {copy.hero.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8316" data-mp="2tlnbgrs1bzrv">
                  {fill(copy.hero.cta)}
                </button>}
              {copy.hero.secondaryCta && <Link to="/webshop" className={btnSecondaryLg} data-id="element-8317" data-mp="cwuhs0ccorbz">
                  {fill(copy.hero.secondaryCta)}
                </Link>}
            </>} data-id="element-8313" data-mp="1vvscyhmdjj09" />

      {/* Hoe het werkt: eerst uitleggen, dan pas aanbieden. Met de cadeaus ernaast. */}
      <section className="bg-shell py-20 sm:py-24" aria-labelledby="routines-how" data-id="element-8318" data-mp="10mf3us3sw4ph">
        <div className="mx-auto grid max-w-content gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8319" data-mp="vpolfy4iwx0w">
          <div className="lg:col-span-8" data-id="element-8320" data-mp="qwvj1gwj20rz">
            <h2 id="routines-how" className="font-display text-[1.9rem] font-light leading-tight text-ink sm:text-[2.25rem]" data-id="element-8321" data-mp="qvu8renc5ibb">
              Zo werkt een protocol
            </h2>
            <ol className="mt-8 grid gap-8 sm:grid-cols-3 sm:gap-6" data-id="element-8322" data-mp="1jfsskfgx7ewn">
              {copy.steps.map((s, i) => <li key={s.title} className="border-t border-ink/15 pt-5" data-id="element-8323" data-mp="1elepdqw0o3hf" data-mpi={i}>
                  {/* De lijst is al genummerd voor schermlezers; de volgorde is hier de inhoud. */}
                  <span aria-hidden="true" className="font-display text-[2rem] font-light leading-none text-clay" data-id="element-8324" data-mp="1465ssirooe5w">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 font-display text-xl leading-snug text-ink" data-id="element-8325" data-mp="u5lmv2t7ylhc">{fill(s.title)}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-8326" data-mp="1hvpox5qred68">{fill(s.body)}</p>
                </li>)}
            </ol>
          </div>

          <div className="lg:col-span-4" data-id="element-8327" data-mp="1mwwxv84n1ieh">
            <h2 className="font-display text-[1.9rem] font-light leading-tight text-ink sm:text-[2.25rem]" data-id="element-8328" data-mp="1854uzvm15wtn">{fill(copy.gifts.heading)}</h2>
            <GiftTierList className="mt-8" data-id="element-8329" data-mp="1d36cwd7pzq3j" />
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8330" data-mp="20ubr6ny0iabq">
              <MessageCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8331" data-mp="3ldnvjdu8m38v" />
              {fill(copy.gifts.note)}
              <span className="sr-only" data-id="element-8332" data-mp="3y7dmd76flyr">{shopLabels.viaWhatsApp}</span>
            </a>
          </div>
        </div>
      </section>

      {complete && <RoutineAnchor routine={complete} data-id="element-8333" data-mp="37cbltk5o6xvf" />}

      {/* Of begin bij je klacht. Met maar één protocol online is er geen "overige": dan alleen vergelijken (vanaf twee). */}
      {(others.length > 0 || routines.length > 1) && <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby={others.length > 0 ? 'other-routines' : undefined} data-id="element-8334" data-mp="3gfcnn8ni62ta">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8335" data-mp="3cekbo7n8xjtb">
            {others.length > 0 && <>
                <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-8336" data-mp="rmi9t31xgxgp">
                  <h2 id="other-routines" className={`${sectionHeading} text-ink lg:col-span-7`} data-id="element-8337" data-mp="3kgfqzfyhm0c1">
                    <HeadingText heading={copy.others.heading} data-id="element-8338" data-mp="3pfjy811upnzq" />
                  </h2>
                  {copy.others.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-8339" data-mp="3uewnesg7375h">{fill(copy.others.body)}</p>}
                </div>

                <ul className={`mt-14 grid gap-6 md:grid-cols-2 ${others.length >= 3 ? 'lg:grid-cols-3' : ''}`} data-id="element-8340" data-mp="2aaugg776p7q">
                  {others.map(r => <li key={r.id} className="h-full bg-shell p-7 lg:p-8" data-id="element-8341" data-mp="36osrl7990bp1">
                      <RoutineCard routine={r} data-id="element-8342" data-mp="36modxxljrybk" />
                    </li>)}
                </ul>
              </>}

            <RoutineCompare heading={fill(copy.compareHeading)} data-id="element-8343" data-mp="3fie92ycss725" />

            <p className="mt-12 max-w-2xl text-[16px] leading-relaxed text-muted" data-id="element-8344" data-mp="2izgehwasvjhg">
              Liever met één product beginnen? Dat kan altijd. Je vindt alles los in de{' '}
              <Link to="/webshop" className="text-ink underline underline-offset-4" data-id="element-8345" data-mp="2myyv4fvl98eh">
                webshop
              </Link>
              . Twijfel je, dan{' '}
              <button type="button" onClick={openQuiz} aria-haspopup="dialog" className="text-ink underline underline-offset-4" data-id="element-8346" data-mp="2ajr2fdgm2hvv">
                kijk ik even mee
              </button>
              .
            </p>
          </div>
        </section>}

      <SubscriptionBlock tone="shell" data-id="element-8347" data-mp="1wieo16h3moc3" />

      <section className="bg-sand py-20 sm:py-24 lg:py-32" data-id="element-8348" data-mp="1wjgd4euy9bct">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8349" data-mp="1rs8078uev7uw">
          <Faq items={shopFaq.slice(0, ROUTINES_FAQ_COUNT)} title={fill(copy.faqTitle)} data-id="element-8350" data-mp="1rqn3pdojdeue" />
        </div>
      </section>

      {/* Voor klanten van de salon: het ene klei-vlak van deze pagina */}
      <section className="bg-clay py-20 text-shell sm:py-24" aria-labelledby="salon-client" data-id="element-8351" data-mp="1it4gq3rc0qfv">
        <div className="mx-auto grid max-w-content gap-8 px-6 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10" data-id="element-8352" data-mp="1drnf2610wzy8">
          <div className="lg:col-span-8" data-id="element-8353" data-mp="18v6utmwy35qf">
            <h2 id="salon-client" className={closingHeading} data-id="element-8354" data-mp="2wpa5dc95ixnh">
              <HeadingText heading={copy.salonClient.heading} accentClassName="text-blush" data-id="element-8355" data-mp="2wnyyv4obnbso" />
            </h2>
            {copy.salonClient.body && <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-shell" data-id="element-8356" data-mp="2npn0uhl4lbq7">{fill(copy.salonClient.body, {
              code: SALON_CODE
            })}</p>}
          </div>
          {copy.salonClient.cta && <div className="lg:col-span-4 lg:flex lg:justify-end" data-id="element-8357" data-mp="2dzk5wbug1np8">
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnOnClay} data-id="element-8358" data-mp="2dx7ktpte2x7y">
                {fill(copy.salonClient.cta)}
                <span className="sr-only" data-id="element-8359" data-mp="24zf189gmx3er">{shopLabels.viaWhatsApp}</span>
              </a>
            </div>}
        </div>
      </section>
    </>;
}